/**
 * 팝업 dismiss 상태 관리.
 *
 * 3단계 dismiss 정책:
 *  1. 세션 dismiss (X 닫기) — sessionStorage, 탭 닫으면 초기화
 *  2. 기간 dismiss (체크박스) — localStorage + 선택한 시간
 *  3. 영구 dismiss (체크박스) — localStorage, 만료 없음
 *
 * 어드민 설정(dismissCooldownHours)은 사용자에게 보여줄 옵션 수준 결정:
 *  - 24  → "오늘 하루 창 열지 않기"만
 *  - 168 → "오늘 하루" + "일주일간"
 *  - null → "오늘 하루" + "일주일간" + "다시 보지 않기"
 *
 * 영구지침: docs/develop/popup/popup-spec.md §9.3
 */

import { safeLocalStorage, safeSessionStorage } from '@/lib/safeStorage'

const KEY_PREFIX = 'cb:popup:'
const SESSION_PREFIX = 'cb:popup:sess:'

// ─────────────────────────────────────────────────────────────────────────────
// 세션 dismiss (X 닫기 — 탭/세션 동안만)
// ─────────────────────────────────────────────────────────────────────────────

export function markSessionDismiss(popupId: number): void {
  safeSessionStorage.setItem(`${SESSION_PREFIX}${popupId}`, 'true')
}

function isSessionDismissed(popupId: number): boolean {
  return safeSessionStorage.getItem(`${SESSION_PREFIX}${popupId}`) === 'true'
}

// ─────────────────────────────────────────────────────────────────────────────
// 기간/영구 dismiss (체크박스 선택 — localStorage)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 사용자가 체크박스로 선택한 기간만큼 dismiss.
 *
 * @param hours 24 = 오늘 하루, 168 = 일주일, null = 영구
 */
export function markDismissedFor(popupId: number, hours: number | null): void {
  safeLocalStorage.setItem(`${KEY_PREFIX}${popupId}:dismissedAt`, String(Date.now()))
  safeLocalStorage.setItem(
    `${KEY_PREFIX}${popupId}:cooldownHours`,
    hours == null ? 'permanent' : String(hours)
  )
}

function isPeriodDismissed(popupId: number): boolean {
  const at = safeLocalStorage.getItem(`${KEY_PREFIX}${popupId}:dismissedAt`)
  if (!at) return false

  const cooldown = safeLocalStorage.getItem(`${KEY_PREFIX}${popupId}:cooldownHours`)
  if (cooldown === 'permanent') return true

  const hours = Number(cooldown)
  if (isNaN(hours) || hours <= 0) return false

  const elapsed = Date.now() - Number(at)
  return elapsed < hours * 60 * 60 * 1000
}

// ─────────────────────────────────────────────────────────────────────────────
// 종합 체크 (PopupHost에서 사용)
// ─────────────────────────────────────────────────────────────────────────────

export function isDismissed(popupId: number): boolean {
  return isSessionDismissed(popupId) || isPeriodDismissed(popupId)
}

// ─────────────────────────────────────────────────────────────────────────────
// 체크박스 옵션 생성
// ─────────────────────────────────────────────────────────────────────────────

export interface DismissOption {
  label: string
  hours: number | null
}

/**
 * 어드민 설정(dismissCooldownHours)에 따라 사용자에게 보여줄 옵션 생성.
 */
export function getDismissOptions(maxHours: number | null): DismissOption[] {
  const options: DismissOption[] = [
    { label: '오늘 하루 창 열지 않기', hours: 24 },
  ]

  if (maxHours == null || maxHours >= 168) {
    options.push({ label: '일주일간 창 열지 않기', hours: 168 })
  }

  if (maxHours == null) {
    options.push({ label: '다시 보지 않기', hours: null })
  }

  return options
}

/** dismiss 기록 전체 제거 (테스트/디버깅용) */
export function clearDismissed(popupId: number): void {
  safeLocalStorage.removeItem(`${KEY_PREFIX}${popupId}:dismissedAt`)
  safeLocalStorage.removeItem(`${KEY_PREFIX}${popupId}:cooldownHours`)
  safeSessionStorage.removeItem(`${SESSION_PREFIX}${popupId}`)
}
